import { Info } from '@crehana/compass.icons.outline/v2/info';
import {
  Drawer,
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerPopup,
  DrawerPortal,
  DrawerTitle,
  DrawerViewport,
} from '@crehana/compass.ui/v3/drawer';
import { Tag } from '@crehana/compass.ui/v3/tag';
import { BasicTooltip } from '@crehana/compass.ui/v3/tooltip';
import { format } from '@crehana/date/format';
import { DATE_FORMATS } from '@crehana/date/presets';
import { parseISO } from 'date-fns';

import { TimeOvertimeRequestStatus } from '@/generated/graphql/types';

import { STATUS_CONFIG } from '../../constants';
import { useRequestDetail } from '../../hooks/use-request-detail';
import { useTranslation } from '../../i18n';
import type { TMyOvertimeRequest } from '../../types';
import { formatMinutes, formatShiftName } from '../../utils';
import { CommentsThread } from './components/comments-thread';
import { DetailRow } from './components/detail-row';
import { RequesterBanner } from './components/requester-banner';
import { RespondedByRow } from './components/responded-by-row';

type TRequestDetailDrawerProps = {
  request: TMyOvertimeRequest | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenChangeComplete?: (open: boolean) => void;
};

export const RequestDetailDrawer = ({
  request,
  open,
  onOpenChange,
  onOpenChangeComplete,
}: TRequestDetailDrawerProps) => {
  const { t, languageCode } = useTranslation();
  const {
    respondedBy,
    comments,
    loading,
    error,
    submitting,
    canComment,
    addComment,
    refetch,
  } = useRequestDetail(request?.id ?? null);

  // AC-3 hides the row while Pendiente, on top of the mock's own
  // `respondedBy` presence — the mock has no real request status to key off,
  // so a Pendiente request must not read as "already answered" once TIM-557
  // starts returning a real `respondedBy` for it.
  const showRespondedBy =
    respondedBy != null &&
    request?.status !== TimeOvertimeRequestStatus.Pending;

  const title = request
    ? t('REQUEST_DETAIL_DRAWER_TITLE', {
        interpolation: {
          date: format({
            date: parseISO(request.request_date),
            pattern: "d 'de' MMMM",
            languageCode,
          }),
        },
      })
    : '';

  // AC-2: the icon only marks a backend-flagged difference. It never carries
  // the excess amount itself (AC-6 forbids that value everywhere).
  const finalAmountAdornment = request?.has_final_amount_difference ? (
    <BasicTooltip content={t('STATUS_UPDATED_TOOLTIP')}>
      <span
        data-testid="final-amount-difference-icon"
        className="tw-inline-flex tw-cds-text-secondary"
      >
        <Info size={16} />
      </span>
    </BasicTooltip>
  ) : undefined;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      size="md"
    >
      <DrawerPortal>
        <DrawerBackdrop />
        <DrawerViewport>
          <DrawerPopup>
            <DrawerContent>
              <DrawerHeader>
                <DrawerTitle>{title}</DrawerTitle>
                <DrawerCloseButton />
              </DrawerHeader>

              {request ? (
                <>
                  <RequesterBanner />
                  <DrawerBody className="tw-flex tw-flex-col tw-cds-gap-6">
                    <div className="tw-grid tw-grid-cols-2 tw-cds-gap-5">
                      <DetailRow
                        label={t('TABLE_HEADER_STATUS')}
                        value={
                          <Tag variant={STATUS_CONFIG[request.status].variant}>
                            {t(STATUS_CONFIG[request.status].tKey)}
                          </Tag>
                        }
                      />
                      <DetailRow
                        label={t('TABLE_HEADER_SHIFT')}
                        value={formatShiftName(request.shift_name)}
                      />
                      <DetailRow
                        label={t('REQUEST_DETAIL_CREATED_AT_LABEL')}
                        value={format({
                          date: parseISO(request.created_at),
                          pattern: DATE_FORMATS.SHORT,
                          languageCode,
                        })}
                      />
                      <DetailRow
                        label={t('REQUEST_DETAIL_OVERTIME_REQUESTED_LABEL')}
                        value={formatMinutes(request.extra_minutes)}
                      />
                      <DetailRow
                        label={t('REQUEST_DETAIL_COMPENSATION_REQUESTED_LABEL')}
                        value={formatMinutes(request.replacement_minutes)}
                      />
                      {request.final_extra_minutes != null ? (
                        <DetailRow
                          label={t('REQUEST_DETAIL_OVERTIME_FINAL_LABEL')}
                          value={formatMinutes(request.final_extra_minutes)}
                          adornment={finalAmountAdornment}
                        />
                      ) : null}
                      {request.final_replacement_minutes != null ? (
                        <DetailRow
                          label={t('REQUEST_DETAIL_COMPENSATION_FINAL_LABEL')}
                          value={formatMinutes(
                            request.final_replacement_minutes,
                          )}
                          adornment={finalAmountAdornment}
                        />
                      ) : null}
                    </div>

                    <DetailRow
                      label={t('REQUEST_DETAIL_REASON_LABEL')}
                      value={request.reason}
                    />

                    {showRespondedBy && respondedBy ? (
                      <RespondedByRow responder={respondedBy} />
                    ) : null}

                    <CommentsThread
                      comments={comments}
                      loading={loading}
                      error={error}
                      submitting={submitting}
                      canComment={canComment}
                      onSubmit={addComment}
                      onRetry={refetch}
                    />
                  </DrawerBody>
                </>
              ) : null}
            </DrawerContent>
          </DrawerPopup>
        </DrawerViewport>
      </DrawerPortal>
    </Drawer>
  );
};
